import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ProfileSettings from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ProfileSettings';
import { StyledDivider } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ProfileForm';
import {
  BaseSettingsForm,
  BaseSettingsFormSchema
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import color from 'src/utils/colors';
import {
  useContentGenerateForSku,
  useGetContentGenerationProfile
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { getQueryParamValue } from 'src/utils/app';
import { RequirementsModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/RequirementsModal';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';

const ContentGenerationForm = () => {
  const methods = useFormContext<BaseSettingsForm>();
  const { formState, getValues, control, reset } = methods;
  const { generateContentForSku, isPending, isSuccess } = useContentGenerateForSku();
  const retailerSku = useAppSelector((state) => state.entityService?.mainEntity?.retailerSku);
  const stacklineSku = useAppSelector((state) => state.entityService?.mainEntity?.stacklineSku);
  const formValues = useWatch({ control });
  const { beaconNavigator } = useBeaconNavigator();
  const contentGenId = getQueryParamValue('contentGenId', '');
  const { contentGenerationProfile, isLoading: isLoadingProfile } = useGetContentGenerationProfile(contentGenId);
  const isReadOnlyMode = !!contentGenId;
  // Load profile data from content generation request if available
  useEffect(() => {
    if (contentGenerationProfile && isReadOnlyMode) {
      // Set form to read-only mode
      Object.keys(methods.getValues()).forEach((key) => {
        methods.register(key as any, { disabled: true });
      });

      reset({
        settings: contentGenerationProfile.settings
      });
    }
  }, [contentGenerationProfile, reset, isReadOnlyMode, methods]);

  useEffect(() => {
    if (isSuccess) {
      // Add a 1-second delay before navigation
      const navigationTimer = setTimeout(() => {
        beaconNavigator.setPathname('/content/generations');
        beaconNavigator.go();
      }, 1000);

      return () => clearTimeout(navigationTimer);
    }
  }, [isSuccess, beaconNavigator]);

  const retailerId = getQueryParamValue('rid', '');

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.back();
  };

  const handleGenerate = () => {
    const formData = getValues();

    generateContentForSku({
      retailerId: Number(retailerId),
      retailerSku,
      stacklineSku,
      useDynamicProfile: true,
      settings: formData.settings
    });
  };

  // Tracking the validity of the form, so we can enable/disable the save button
  // use this for debugging only
  useEffect(() => {
    if (!formState.isValid) {
      const result = BaseSettingsFormSchema.safeParse(formValues);
    }
  }, [formValues, formState.isValid]);

  if (isLoadingProfile) {
    return (
      <Flex justifyContent="center" alignItems="center" sx={{ height: '400px' }}>
        <div>Loading...</div>
      </Flex>
    );
  }

  return (
    <>
      <StyledDivider />
      <ProfileSettings<BaseSettingsForm> />
      <Flex
        justifyContent="space-between"
        sx={{
          marginTop: '64px'
        }}
      >
        <SlButton
          buttonSx={{
            border: `1px solid ${color.graysSecondary}`
          }}
          onClick={handleBack}
          disabled={isPending}
        >
          Back
        </SlButton>
        {!isReadOnlyMode && (
          <Flex gap={16}>
            <SlButton
              variant="contained"
              type="submit"
              disabled={!formState.isValid || isPending}
              onClick={handleGenerate}
              sx={{
                opacity: isPending ? 0.7 : 1
              }}
            >
              {isPending ? 'Generating...' : 'Generate'}
            </SlButton>
          </Flex>
        )}
      </Flex>
      {!isReadOnlyMode && <RequirementsModal<BaseSettingsForm> />}
    </>
  );
};

export default ContentGenerationForm;
